import { useRef, useState } from "react"
import { cn } from "@/lib/utils"
import { PRODUCTS } from "../data"

interface Props {
  value: string
  onChange: (value: string) => void
  onSubmit: () => void // Ctrl+Enter or Cmd+Enter; plain Enter starts a new line
  placeholder?: string
  className?: string
}

// Multi-line text box where typing # opens a product search. Picking a
// product writes "#SKU " into the text, which NoteText later shows as a link.
export default function MentionInput({ value, onChange, onSubmit, placeholder, className }: Props) {
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const [query, setQuery] = useState<string | null>(null)
  const [highlight, setHighlight] = useState(0)

  const matches = query == null ? [] : PRODUCTS.filter((p) =>
    `${p.sku} ${p.name}`.toLowerCase().includes(query.toLowerCase()),
  ).slice(0, 6)

  const readQuery = (text: string, caret: number) => {
    const found = text.slice(0, caret).match(/#([A-Za-z0-9-]*)$/)
    setQuery(found ? found[1] : null)
    setHighlight(0)
  }

  const pick = (sku: string) => {
    const input = inputRef.current
    const caret = input?.selectionStart ?? value.length
    const before = value.slice(0, caret).replace(/#[A-Za-z0-9-]*$/, `#${sku} `)
    const next = before + value.slice(caret)
    onChange(next)
    setQuery(null)
    requestAnimationFrame(() => input?.setSelectionRange(before.length, before.length))
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (matches.length > 0) {
      if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((highlight + 1) % matches.length); return }
      if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((highlight - 1 + matches.length) % matches.length); return }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); pick(matches[highlight].sku); return }
      if (e.key === "Escape") { e.preventDefault(); setQuery(null); return }
    }
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); onSubmit() }
  }

  return (
    <div className={cn("relative", className)}>
      <textarea
        ref={inputRef}
        rows={3}
        value={value}
        placeholder={placeholder}
        onChange={(e) => { onChange(e.target.value); readQuery(e.target.value, e.target.selectionStart ?? 0) }}
        onKeyDown={onKeyDown}
        onBlur={() => setTimeout(() => setQuery(null), 150)}
        className="block w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm outline-none placeholder:text-muted-foreground focus:border-ring"
      />
      {matches.length > 0 && (
        <div className="absolute left-0 top-full z-50 mt-1 w-80 rounded-md border bg-popover p-1 shadow-md">
          {matches.map((p, i) => (
            <div
              key={p.id}
              onMouseDown={(e) => { e.preventDefault(); pick(p.sku) }}
              onMouseEnter={() => setHighlight(i)}
              className={cn("flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm", i === highlight && "bg-accent")}
            >
              <span className="font-medium">#{p.sku}</span>
              <span className="truncate">{p.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
